import { useState } from 'react';

interface Property {
  id: string;
  title: string;
  price: number;
  sale_price?: number;
  rent_price?: number;
  currency?: string;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  property_type: string;
  status: string;
  available_for_sale?: boolean;
  available_for_rent?: boolean;
  images: string[];
  description: string;
  features: string[];
  year_built?: number;
  parking?: number;
  floor?: number;
  furnished?: boolean;
  city: string;
  address?: string;
  videos?: string[];
  latitude?: number;
  longitude?: number;
  is_featured?: boolean;
  maintenance_fee?: number;
  has_maintenance_fee?: boolean;
  included_services?: string[];
}

interface PropertyRequestFormProps {
  property: Property;
  requestType: 'compra' | 'renta';
  onClose: () => void;
}

export default function PropertyRequestForm({ property, requestType, onClose }: PropertyRequestFormProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    correo: '',
    telefono: '',
    medio_contacto: '',
    dia_preferido: '',
    horario_preferido: '',
    requiere_visita: false,
    fecha_visita: '',
    hora_visita: '',
    mensaje_adicional: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const formatPrice = (price: number, currency: string = 'MXN') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    } else {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
  };

  const getPropertyPrice = () => {
    const currency = property.currency || 'MXN';
    if (requestType === 'compra') {
      return formatPrice(property.sale_price || property.price || 0, currency);
    } else {
      return formatPrice(property.rent_price || property.price || 0, currency) + '/mes';
    }
  };

  const getRequestTypeLabel = () => {
    return requestType === 'compra' ? 'Compra' : 'Renta';
  };

  const getRequestTypeIcon = () => {
    return requestType === 'compra' ? 'ri-price-tag-3-line' : 'ri-key-line';
  };

  const getRequestTypeColor = () => {
    return requestType === 'compra' ? 'text-green-600' : 'text-blue-600';
  };

  const getRequestTypeBg = () => {
    return requestType === 'compra' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200';
  };

  const getRequestTypeButton = () => {
    return requestType === 'compra' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700';
  };

  // Generar fechas disponibles para visita (próximos 30 días, excluyendo domingos)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Excluir domingos (0 = domingo)
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        });
      }
    }
    
    return dates;
  };

  // Horarios disponibles para visita
  const availableTimes = [
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' },
    { value: '17:00', label: '5:00 PM' },
    { value: '18:00', label: '6:00 PM' }
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validar campos requeridos
      if (!formData.nombre.trim()) {
        alert('Por favor ingrese su nombre');
        return;
      }
      if (!formData.correo.trim()) {
        alert('Por favor ingrese su correo electrónico');
        return;
      }
      if (!formData.telefono.trim()) {
        alert('Por favor ingrese su teléfono');
        return;
      }
      if (!formData.medio_contacto) {
        alert('Por favor seleccione su medio de contacto preferido');
        return;
      }
      if (formData.requiere_visita && (!formData.fecha_visita || !formData.hora_visita)) {
        alert('Por favor complete la información de la visita');
        return;
      }
      if (formData.mensaje_adicional.length > 500) {
        alert('El mensaje adicional no puede exceder 500 caracteres');
        return;
      }

      // 1. Preparar datos para el CRM (Base de datos)
      const leadData = {
        property_id: property.id,
        property_title: property.title,
        property_price: getPropertyPrice(),
        property_location: property.location,
        request_type: requestType,
        full_name: formData.nombre,
        company: formData.empresa || null,
        email: formData.correo,
        phone: formData.telefono,
        preferred_contact: formData.medio_contacto || 'email', // Valor por defecto
        preferred_day: formData.dia_preferido || null,
        preferred_time: formData.horario_preferido || null,
        requires_visit: formData.requiere_visita,
        visit_date: formData.requiere_visita ? formData.fecha_visita : null,
        visit_time: formData.requiere_visita ? formData.hora_visita : null,
        additional_message: formData.mensaje_adicional || null,
        status: 'nuevo',
        priority: 'media',
        source: 'website_form',
        created_at: new Date().toISOString()
      };

      // 2. Guardar en la base de datos del CRM
      const crmResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(leadData)
      });

      if (!crmResponse.ok) {
        console.error('Error al guardar en CRM:', await crmResponse.text());
        // Continuar con el envío del email aunque falle el CRM
      }

      // 3. Preparar datos para envío por email
      const submitData = new URLSearchParams();
      submitData.append('tipo_solicitud', getRequestTypeLabel());
      submitData.append('propiedad_id', property.id);
      submitData.append('propiedad_titulo', property.title);
      submitData.append('propiedad_precio', getPropertyPrice());
      submitData.append('propiedad_ubicacion', property.location);
      submitData.append('nombre', formData.nombre);
      submitData.append('empresa', formData.empresa || 'No especificada');
      submitData.append('correo', formData.correo);
      submitData.append('telefono', formData.telefono);
      submitData.append('medio_contacto', formData.medio_contacto);
      submitData.append('dia_preferido', formData.dia_preferido || 'No especificado');
      submitData.append('horario_preferido', formData.horario_preferido || 'No especificado');
      submitData.append('requiere_visita', formData.requiere_visita ? 'Sí' : 'No');
      
      if (formData.requiere_visita) {
        submitData.append('fecha_visita', formData.fecha_visita);
        submitData.append('hora_visita', formData.hora_visita);
      } else {
        submitData.append('fecha_visita', 'No aplica');
        submitData.append('hora_visita', 'No aplica');
      }
      
      submitData.append('mensaje_adicional', formData.mensaje_adicional || 'Sin mensaje adicional');

      // 4. Enviar formulario por email
      const emailResponse = await fetch('https://readdy.ai/api/form/d4bmd6362rkouuf3qf40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (emailResponse.ok) {
        setShowSuccess(true);
        // Cerrar después de 3 segundos
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        alert('Error al enviar la solicitud. Por favor intenta nuevamente.');
      }
      
    } catch (error) {
      console.error('Error al enviar solicitud:', error);
      alert('Error al enviar la solicitud. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 ${requestType === 'compra' ? 'bg-green-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <i className={`${getRequestTypeIcon()} text-3xl ${getRequestTypeColor()}`}></i>
          </div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-2">¡Solicitud Enviada!</h3>
          <p className="text-gray-600 mb-4">
            Hemos recibido tu solicitud de {requestType}. Nos pondremos en contacto contigo pronto.
          </p>
          <div className={`${getRequestTypeBg()} rounded-lg p-4 text-left`}>
            <h4 className="font-medium text-gray-800 mb-2">Resumen de tu Solicitud</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Tipo:</strong> {getRequestTypeLabel()}</p>
              <p><strong>Propiedad:</strong> {property.title}</p>
              <p><strong>Precio:</strong> {getPropertyPrice()}</p>
              <p><strong>Contacto:</strong> {formData.nombre}</p>
              <p><strong>Email:</strong> {formData.correo}</p>
              {formData.requiere_visita && (
                <p><strong>Visita:</strong> {formData.fecha_visita} a las {formData.hora_visita}</p>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">Cerrando automáticamente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 ${requestType === 'compra' ? 'bg-green-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center`}>
              <i className={`${getRequestTypeIcon()} text-2xl ${getRequestTypeColor()}`}></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Solicitud de {getRequestTypeLabel()}
              </h2>
              <p className="text-gray-600">{property.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[calc(95vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
            {/* Property Info */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-6 sticky top-0">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Información de la Propiedad</h3>
                
                {/* Property Image */}
                <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden mb-4">
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Property Details */}
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-800">{property.title}</h4>
                    <p className="text-sm text-gray-600 flex items-center">
                      <i className="ri-map-pin-line mr-1"></i>
                      {property.location}
                    </p>
                  </div>

                  <div className={`p-3 ${getRequestTypeBg()} rounded-lg`}>
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${getRequestTypeIcon()} ${getRequestTypeColor()}`}></i>
                      <span className={`font-medium ${getRequestTypeColor()}`}>
                        {getRequestTypeLabel()}
                      </span>
                    </div>
                    <div className={`text-xl font-bold ${getRequestTypeColor()}`}>
                      {getPropertyPrice()}
                    </div>
                  </div>

                  {/* Property Features */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {property.bedrooms > 0 && (
                      <div className="flex items-center gap-2">
                        <i className="ri-hotel-bed-line text-gray-500"></i>
                        <span>{property.bedrooms} hab.</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <i className="ri-drop-line text-gray-500"></i>
                      <span>{property.bathrooms} baños</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-square-line text-gray-500"></i>
                      <span>{property.area} m²</span>
                    </div>
                    {property.parking && property.parking > 0 && (
                      <div className="flex items-center gap-2">
                        <i className="ri-car-line text-gray-500"></i>
                        <span>{property.parking} est.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form 
                id="property-request-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Información Personal */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Personal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => handleInputChange('nombre', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Tu nombre completo"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Empresa (Opcional)
                      </label>
                      <input
                        type="text"
                        value={formData.empresa}
                        onChange={(e) => handleInputChange('empresa', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Nombre de tu empresa"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correo Electrónico *
                      </label>
                      <input
                        type="email"
                        value={formData.correo}
                        onChange={(e) => handleInputChange('correo', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="tu@email.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        value={formData.telefono}
                        onChange={(e) => handleInputChange('telefono', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="+52 123 456 7890"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Preferencias de Contacto */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Preferencias de Contacto</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Medio de Contacto Preferido *
                      </label>
                      <select
                        value={formData.medio_contacto}
                        onChange={(e) => handleInputChange('medio_contacto', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                        required
                      >
                        <option value="">Seleccionar medio</option>
                        <option value="telefono">Teléfono</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">Email</option>
                        <option value="videollamada">Videollamada</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Día Preferido
                      </label>
                      <select
                        value={formData.dia_preferido}
                        onChange={(e) => handleInputChange('dia_preferido', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Cualquier día</option>
                        <option value="lunes">Lunes</option>
                        <option value="martes">Martes</option>
                        <option value="miercoles">Miércoles</option>
                        <option value="jueves">Jueves</option>
                        <option value="viernes">Viernes</option>
                        <option value="sabado">Sábado</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Horario Preferido
                      </label>
                      <select
                        value={formData.horario_preferido}
                        onChange={(e) => handleInputChange('horario_preferido', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Cualquier horario</option>
                        <option value="mañana">Mañana (9:00 - 12:00)</option>
                        <option value="tarde">Tarde (12:00 - 18:00)</option>
                        <option value="noche">Noche (18:00 - 21:00)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Visita a la Propiedad */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="requiere_visita"
                      checked={formData.requiere_visita}
                      onChange={(e) => handleInputChange('requiere_visita', e.target.checked)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="requiere_visita" className="text-lg font-semibold text-gray-800">
                      Quiero agendar una visita a la propiedad
                    </label>
                  </div>

                  {formData.requiere_visita && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-2">
                            Fecha Preferida para la Visita *
                          </label>
                          <select
                            value={formData.fecha_visita}
                            onChange={(e) => handleInputChange('fecha_visita', e.target.value)}
                            className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                            required={formData.requiere_visita}
                          >
                            <option value="">Seleccionar fecha</option>
                            {generateAvailableDates().map(date => (
                              <option key={date.value} value={date.value}>
                                {date.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-2">
                            Hora Preferida *
                          </label>
                          <select
                            value={formData.hora_visita}
                            onChange={(e) => handleInputChange('hora_visita', e.target.value)}
                            className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                            required={formData.requiere_visita}
                          >
                            <option value="">Seleccionar hora</option>
                            {availableTimes.map(time => (
                              <option key={time.value} value={time.value}>
                                {time.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                        <div className="flex gap-2">
                          <i className="ri-information-line text-blue-600 text-sm mt-0.5"></i>
                          <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">Información sobre las visitas:</p>
                            <ul className="space-y-1 text-xs">
                              <li>• Las visitas tienen una duración aproximada de 30-45 minutos</li>
                              <li>• Confirmaremos la cita por el medio de contacto seleccionado</li>
                              <li>• Puedes reagendar con 24 horas de anticipación</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mensaje Adicional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje Adicional (Opcional)
                  </label>
                  <textarea
                    value={formData.mensaje_adicional}
                    onChange={(e) => handleInputChange('mensaje_adicional', e.target.value)}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                    placeholder="Cuéntanos más sobre tus necesidades, presupuesto, tiempos, o cualquier pregunta específica..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.mensaje_adicional.length}/500 caracteres
                  </p>
                </div>

                {/* Información Importante */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <i className="ri-shield-check-line text-green-600 text-lg mt-0.5"></i>
                    <div className="text-sm">
                      <h4 className="font-medium text-gray-800 mb-2">Tu información está segura</h4>
                      <ul className="space-y-1 text-gray-600">
                        <li>• Nos pondremos en contacto contigo en un plazo máximo de 24 horas</li>
                        <li>• Tu información será enviada directamente a nuestro equipo de ventas</li>
                        <li>• No compartimos tus datos con terceros</li>
                        <li>• Recibirás una copia de tu solicitud por email</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 px-6 py-3 ${getRequestTypeButton()} text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Enviar Solicitud de {getRequestTypeLabel()}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
